import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { create } from "zustand";
import { persist, createJSONStorage, type StateStorage } from "zustand/middleware";

import { UserRole, ROLE_HIERARCHY } from "@/constants/user";

import type { User } from "@/api/types";

// User 타입은 @/api/types에서 import

interface UserState {
  // 현재 사용자 정보
  currentUser: User | null;
  isAuthenticated: boolean;
  isPersistent?: boolean;
  accessToken?: string | null;

  // 액션들
  login: (user: User, keep_login?: boolean, accessToken?: string) => void;
  logout: (clearLastPath?: boolean) => void;
  updateUser: (updates: Partial<User>) => void;
  setUserRole: (role: UserRole) => void;

  // 권한 체크 함수들
  hasRole: (role: UserRole) => boolean;
  hasRoleOrHigher: (role: UserRole) => boolean;
  isSuperAdmin: () => boolean;
  isAdmin: () => boolean;
  isSuperUser: () => boolean;
  isRegularUser: () => boolean;

  // 권한별 기능 접근 체크
  canManageUsers: () => boolean;
  canAccessAdminPanel: () => boolean;
  canViewAnalytics: () => boolean;
  canEditContent: () => boolean;
  canEditCompanyInfo: () => boolean;

  // 네비게이션 메뉴 접근 권한
  canAccessCreate: () => boolean;
  canAccessCredits: () => boolean;
  canAccessUserManagement: () => boolean;
  canAccessSales: () => boolean;
}

// defaultUsers는 @/constants/user에서 import

const customStorage: StateStorage = {
  getItem: (name: string) => {
    return localStorage.getItem(name);
  },
  setItem: (name: string, value: string) => {
    localStorage.setItem(name, value);
  },
  removeItem: (name: string) => {
    localStorage.removeItem(name);
  },
};

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      currentUser: null,
      isAuthenticated: false,
      isPersistent: false,
      accessToken: null,

      login: (user: User, keep_login?: boolean, accessTokenArg?: string) => {
        const accessToken = accessTokenArg || Cookies.get("accessToken");
        let userId = user.user_id;

        if (accessToken) {
          const decoded = jwtDecode<{ sub?: string | number }>(accessToken);
          if (decoded) {
            if (decoded.sub !== undefined) {
              // sub 값을 16진수에서 10진수로 변환
              if (typeof decoded.sub === "string") {
                const decimalValue = Number.parseInt(decoded.sub, 16);
                userId = decimalValue;
              } else {
                userId = decoded.sub;
              }
            }
          }
        }

        set({
          currentUser: { ...user, user_id: userId, lastLogin: new Date() },
          isAuthenticated: true,
          isPersistent: keep_login ?? false,
          accessToken: accessToken,
        });
      },

      logout: (clearLastPath = true) => {
        set({
          currentUser: null,
          isAuthenticated: false,
          isPersistent: false,
          accessToken: null,
        });
        if (clearLastPath) {
          Cookies.remove("lastPath");
        }
        useUserStore.persist.clearStorage();
      },

      updateUser: (updates: Partial<User>) => {
        const currentUser = get().currentUser;
        if (currentUser) {
          const accessToken = Cookies.get("accessToken");
          let userId = updates.user_id ?? currentUser.user_id;

          if (accessToken && !updates.user_id) {
            const decoded = jwtDecode<{ sub?: string | number }>(accessToken);
            if (decoded) {
              if (decoded.sub !== undefined) {
                // sub 값을 16진수에서 10진수로 변환
                if (typeof decoded.sub === "string") {
                  const decimalValue = Number.parseInt(decoded.sub, 16);
                  userId = decimalValue;
                } else {
                  userId = decoded.sub;
                }
              }
            }
          }

          set({
            currentUser: { ...currentUser, ...updates, user_id: userId },
          });
        }
      },

      setUserRole: (role: UserRole) => {
        const currentUser = get().currentUser;
        if (currentUser) {
          set({
            currentUser: { ...currentUser, role },
          });
        }
      },

      // 권한 체크 함수들
      hasRole: (role: UserRole) => {
        const currentUser = get().currentUser;
        return currentUser?.role === role;
      },

      hasRoleOrHigher: (role: UserRole) => {
        const currentUser = get().currentUser;
        if (!currentUser) {
          return false;
        }

        const currentRoleLevel = ROLE_HIERARCHY[currentUser.role];
        const requiredRoleLevel = ROLE_HIERARCHY[role];

        return currentRoleLevel >= requiredRoleLevel;
      },

      isSuperAdmin: () => get().hasRole(UserRole.A) || get().hasRole(UserRole.AC),
      isAdmin: () => get().hasRole(UserRole.AS),
      isSuperUser: () => get().hasRole(UserRole.U) || get().hasRole(UserRole.UC),
      isRegularUser: () => get().hasRole(UserRole.US),

      // 기능별 권한 체크
      canManageUsers: () => get().hasRoleOrHigher(UserRole.AS),
      canAccessAdminPanel: () => get().hasRoleOrHigher(UserRole.AS),
      canViewAnalytics: () => get().hasRoleOrHigher(UserRole.U) || get().hasRole(UserRole.UC),
      canEditContent: () => get().hasRoleOrHigher(UserRole.US),
      canEditCompanyInfo: () => get().isSuperAdmin() || get().isSuperUser(), // A, AC, U, UC만 수정 가능

      // 네비게이션 메뉴 접근 권한
      canAccessCreate: () => get().hasRoleOrHigher(UserRole.US), // US 이상 (US, U, AS, A)
      canAccessCredits: () => get().hasRoleOrHigher(UserRole.U) || get().hasRole(UserRole.UC), // U 이상 (U, AS, A)
      canAccessUserManagement: () =>
        get().hasRoleOrHigher(UserRole.U) || get().hasRole(UserRole.UC), // U 이상 (U, AS, A)
      canAccessSales: () => get().hasRoleOrHigher(UserRole.AS), // AS 이상 (A, AS)
    }),
    {
      name: "user-storage",
      storage: createJSONStorage(() => customStorage),
      partialize: state => ({
        currentUser: state.currentUser,
        isAuthenticated: state.isAuthenticated,
        isPersistent: state.isPersistent,
        accessToken: state.accessToken,
      }),
    }
  )
);

// roleDisplayText, getDefaultUser, getAllDefaultUsers는 @/constants/user에서 import
